import gettext from '@sdk/lang/gettext';
import getCurrentBreakStartTime from '@sdk/automatedBreaks/helpers/getCurrentBreakStartTime';
import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';
import { shouldApplyAutomatedBreaksForShift } from '@sdk/automatedBreaks/utils';

import canEndBreak from '@web/timeClock/utils/canEndBreak';
import getBreakDescription from '@web/timeClock/utils/getBreakDescription';

import config from '@angular/config';
import ClockInCtrl from '@angular/dashboard/components/ClockInCtrl';
import modalShiftViewUrl from '@angular/shifts/modal-shift-view.html';
import modalClockInUrl from '@angular/dashboard/components/modal-clock-in.html';
import nextShiftsComponentUrl from '@angular/dashboard/components/nextShiftsComponent.html';

class NextShiftsCtrl {
  shouldShowBackTooSoonModal = false;

  constructor(
    Authentication,
    AccountService,
    EventCollection,
    Restangular,
    Utils,
    extendShiftModel,
    toDateLongFilter,
    toTimeFilter,
    TimesheetsUtils,
    ShiftUtils,
    $uibModal,
    $rootScope,
    $q
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      AccountService,
      EventCollection,
      Restangular,
      Utils,
      extendShiftModel,
      toDateLongFilter,
      toTimeFilter,
      TimesheetsUtils,
      ShiftUtils,
      $uibModal,
      $rootScope,
      $q,
    });

    this.AUTOMATED_BREAKS_ENABLED = isFeatureEnabled(
      config.AUTOMATED_BREAKS_ENABLED,
      Authentication.user.org.id
    );

    this.REDESIGNED_CLOCK_IN_MODAL_ENABLED = isFeatureEnabled(
      config.REDESIGNED_CLOCK_IN_MODAL_ENABLED,
      Authentication.user.org.id
    );
  }

  async openShift(shift) {
    if (shift.clockedOut) {
      return this.openClockInModal(shift);
    }

    await this.ShiftUtils.loadAsync();
    this.$uibModal
      .open({
        templateUrl: modalShiftViewUrl,
        controllerProvider: async () =>
          (await import(/* webpackChunkName: "shifts" */ '../../shifts/ViewShiftCtrl')).default,
        controllerAs: 'ctrl',
        size: '620',
        resolve: {
          shift: () => shift,
          /*
          Dummy notification object. The EDIT button will be hidden but
          "make shift available" and "offer shift" buttons will be shown
        */
          notification: () => ({ items: [], type: 'shift_assigned' }),
          activityLogItem: () => null,
          fromExchange: () => false,
        },
      })
      .result.ignoreCancel();
  }

  async openClockInModal(shift) {
    if (!this.Authentication.user.org.isPremium) {
      return;
    }

    await this.ShiftUtils.loadAsync();

    let shifts = [];
    if (shift) {
      shifts = [await this.EventCollection.getEvent(shift.id, { includeTimesheets: 'full' })];
    } else if (this.shifts.length) {
      shifts = await this.EventCollection.getClockIn();
    }
    shifts = shifts.map((s) => this.Restangular.restangularizeElement(null, s, 'calendar'));

    const canClockInShiftless = this.Authentication.user.orgSettings.clockInAdHocEnabled;

    this.onClockInNotAvailableModalClose = () => (this.isClockInNotAvailableModalVisible = false);

    if (shifts.length || canClockInShiftless) {
      const lastLocationAndPosition = !shifts.length
        ? await this.EventCollection.getLastClockIn()
        : {};
      const modalInstance = this.$uibModal.open({
        templateUrl: modalClockInUrl,
        controller: ClockInCtrl,
        controllerAs: 'ctrl',
        windowClass: 'window-confirmation-modal',
        size: '620',
        resolve: {
          shifts: () => shifts,
          upcomingShifts: () => this.shifts,
          readOnly: () => false,
          lastLocationAndPosition: () => lastLocationAndPosition,
        },
      });

      const { openConfirmationModal } = await modalInstance.result;
      if (openConfirmationModal) {
        this.confirmationModalState = {
          isVisible: true,
          title: gettext('Clocked in'),
        };
      }

      modalInstance.result.ignoreCancel();
    } else {
      this.isClockInNotAvailableModalVisible = true;
      this.upcomingShifts = this.shifts;
      this.upcomingShiftStartDate = this.shifts[0]?.dtstart;
    }
  }

  takeBreak() {
    return this.EventCollection.takeBreak();
  }

  endBreak(shift) {
    if (
      canEndBreak({
        automatedBreaksFeatureFlag: this.AUTOMATED_BREAKS_ENABLED,
        automatedBreaksEnabled: this.shouldAutomatedBreaksApply(shift),
        shift,
      })
    ) {
      return this.EventCollection.endBreak();
    }
    this.shouldShowBackTooSoonModal = true;
    return this.$q.reject();
  }

  get clockInEnabled() {
    return (
      this.Authentication?.isOrgFeatureEnabled('clockIn') &&
      this.Authentication.user.timeclockEnabled &&
      this.Authentication.user.orgSettings.clockInEnabled &&
      this.Authentication.user.orgSettings.clockInFromWebAndApps
    );
  }

  get clockInFromWebAndApps() {
    return (
      this.Authentication?.isOrgFeatureEnabled('clockIn') &&
      this.Authentication.user.orgSettings.clockInFromWebAndApps
    );
  }

  get now() {
    return this.Utils.getNowDate();
  }

  shouldAutomatedBreaksApply(shift) {
    return shouldApplyAutomatedBreaksForShift({
      shift,
      isAutomatedBreakEnabled:
        this.AUTOMATED_BREAKS_ENABLED &&
        this.Authentication.user.orgSettings.automatedBreaksIsEnabled,
    });
  }

  getShiftDescription(shift) {
    let when;

    if (this.Utils.formatDate(shift.dtstart) === this.Utils.formatDate(this.now)) {
      when = 'today';
    } else if (
      this.Utils.formatDate(shift.dtstart) ===
      this.Utils.formatDate(this.Utils.addDays(this.now, 1))
    ) {
      when = 'tomorrow';
    } else {
      when = `on ${this.toDateLongFilter(shift.dtstart)}`;
    }

    let prefix = '';

    if (shift.clockInStatus?.clockedIn) {
      prefix = 'Your shift is in progress';
    } else if (shift.openEnd) {
      prefix = `Your next shift starts ${when}`;
    } else {
      prefix = `Your next shift is ${when}`;
    }

    const suffix = shift.openEnd
      ? this.toTimeFilter(shift.dtstart)
      : `${this.toTimeFilter(shift.dtstart)} - ${this.toTimeFilter(shift.dtend)}`;

    // \xa0 is &nbsp;
    return `${prefix}\xa0•\xa0${suffix}`;
  }

  canEditTimesheet(shift) {
    return this.TimesheetsUtils.canEditTimesheet(shift);
  }

  rowClick(event, shift) {
    // Only use this logic if user clicks on row and not a button inside row
    if (!event.target.classList.contains('notification-info')) {
      return;
    }

    if (shift.clockInStatus.clockedIn && this.canEditTimesheet(shift)) {
      this.openClockInModal(shift);
    }
  }

  /**
   * This method is fired as a callback from choosing break type modal
   */
  async onStartAutomatedBreak(automatedType) {
    const result = await this.EventCollection.takeBreak(false, { automatedType });
    return result;
  }

  getShiftBreakStart(shift) {
    return getCurrentBreakStartTime(shift, this.Authentication.user.settings.usTimeFormat);
  }

  getShiftBreakStartDescription(shift) {
    const startBreakTime = this.getShiftBreakStart(shift);
    return startBreakTime ? `Started break at ${startBreakTime}` : '';
  }

  getShiftOnBreakDescription(shift) {
    return getBreakDescription({
      automatedBreaksFeatureFlag: this.AUTOMATED_BREAKS_ENABLED,
      automatedBreaksEnabled: this.Authentication.user.orgSettings.automatedBreaksIsEnabled,
      shift,
    });
  }

  closeBackTooSoonModal() {
    this.shouldShowBackTooSoonModal = false;
  }

  async onCommentSave(shiftId, comment) {
    this.shifts = this.shifts.map((shift) => {
      if (shift.id === shiftId) {
        return {
          ...shift,
          assigneeNotes: comment,
        };
      }
      return shift;
    });
  }

  clockOut = async (coords, attestations) => {
    try {
      const { longitude, latitude } = coords;
      await this.EventCollection.clockOut({ longitude, latitude, attestations });

      this.confirmationModalState = {
        isVisible: true,
        title: gettext('Clocked out'),
      };
    } catch (error) {
      if (error.status === 406 && error.data.shift) {
        this.EventCollection.addOrEditEvents([error.data.shift]);
      }
      throw error;
    }
  };

  onConfirmationClose = () => {
    this.confirmationModalState.isVisible = false;
    this.confirmationModalState.title = '';
  };

  previewTask = (taskInstance) => {
    const self = this;
    this.isTaskModalVisible = true;

    this.$uibModal
      .open({
        template:
          '<view-task-instance modal="modal" task-instance="selectedTaskInstance" is-preview="true" parent-modal="parentModal"></view-task-instance>',
        size: '620',
        windowClass: 'z-index-2000',
        controller: function TaskInstanceViewOrEditStateCtrl($scope, $uibModalInstance) {
          'ngInject';

          $scope.modal = $uibModalInstance;
          $scope.parentModal = self.parentModal;
          $scope.selectedTaskInstance = $scope.$resolve.selectedTaskInstance;
        },
        resolve: {
          selectedTaskInstance: () => taskInstance,
        },
      })
      .result.ignoreCancel()
      .finally(() => {
        this.isTaskModalVisible = false;
      });
  };
}

export default {
  templateUrl: nextShiftsComponentUrl,
  controller: NextShiftsCtrl,
  controllerAs: 'ctrl',
  bindings: {
    shifts: '<',
  },
};
